import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { catchError, combineLatest, defer, filter, map, mergeMap, Observable, of, skipWhile, switchMap, takeUntil, tap } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { I18nService } from '../i18n/i18n.service';
import { HttpErrorResponse } from '@angular/common/http';
import { AppFeatureService } from '../app-feature/app-feature.service';
import { MyWorkplaceService } from '../myworkplace/my-workplace.service';
import { ToastService } from '../services/toast.service';
import { TooltipService } from '../tooltip/tooltip.service';
import {
    copyUrlToClipboard,
    displayErrorDialogWithUUID,
    displayToastError,
    displayToastInfo,
    displayToastSuccess,
    displayToastWarning,
    getAppFeaturesFail,
    getAppFeaturesSuccess,
    getExternalHelpLink,
    getExternalHelpLinkSuccess,
    invalidateSession,
    invalidateSessionFail,
    invalidateSessionSuccess,
    loadAppContext,
    loadAppContextFail,
    loadAppContextSuccess,
    loadInitializerData,
    loadOAuthPropertiesFail,
    loadOAuthPropertiesSuccess,
    newFromClipboard,
    newFromClipboardFail,
    newFromClipboardLoaded,
    newFromClipboardSuccess,
    redirectToMyWorkplaceIfApplicableFail,
    redirectToMyWorkplaceIfApplicableSuccess,
    searchTooltipsFail,
    searchTooltipsSuccess
} from './core.actions';
import { selectMyWorkplaceFeatures, selectUserPrefs } from './core.selectors';
import { Action, Store } from '@ngrx/store';
import { HelpService } from '../services/help.service';
import { NavigationUtils } from '../../shared/utils/navigation-utils';
import { SystemError } from 'src/app/shared/models/SystemError';
import { ConfirmationService } from 'primeng/api';
import { concatLatestFrom } from '@ngrx/operators';
import { MyWorkplaceUrls } from '../myworkplace/models/MyWorkplaceUrls';
import { Roles } from '../../user-admin/roles/models/Roles';
import { copyUrlToClipboardMaxValidVersion } from 'src/app/analysis/state/analysis.actions';
import { AnalysisService } from 'src/app/analysis/analysis.service';
import { ValidationUtils } from '../validations/validation-utils';
import { TranslationUtils } from 'src/app/shared/utils/translation-utils';
import { exportToExcelFail } from '../../analysis-search/state/analysis-search.actions';
import { AnalysisUtils } from 'src/app/analysis/utils/analysis-utils';
import { HTTPUtils } from '../../shared/utils/http-utils';

@Injectable()
export class CoreEffects {
    constructor(
        private readonly store: Store,
        private readonly authService: AuthService,
        private readonly actions$: Actions,
        private readonly translate: TranslateService,
        private readonly i18nService: I18nService,
        private readonly tooltipService: TooltipService,
        private readonly toastService: ToastService,
        private readonly appFeatureService: AppFeatureService,
        private readonly myWorkplaceService: MyWorkplaceService,
        private readonly helpService: HelpService,
        private readonly confirmationService: ConfirmationService,
        private readonly analysisService: AnalysisService,
        private readonly router: Router
    ) {}

    invalidateSession$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(invalidateSession),
            switchMap(() =>
                this.authService.invalidateSession().pipe(
                    map(() => invalidateSessionSuccess()),
                    catchError(() => of(invalidateSessionFail))
                )
            )
        );
    });

    loadAppContext$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(loadOAuthPropertiesSuccess, loadAppContext),
            switchMap(() =>
                this.authService.getAppContext().pipe(
                    switchMap(appContext => this.translate.use(appContext.userPrefs.language).pipe(map(() => loadAppContextSuccess({ appContext })))),
                    catchError(() => of(loadAppContextFail()))
                )
            )
        );
    });

    loadOAuthProperties$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(loadInitializerData),
            switchMap(() =>
                this.authService.loadOAuthProperties().pipe(
                    map(oAuthProperties => loadOAuthPropertiesSuccess({ oAuthProperties })),
                    catchError(() => of(loadOAuthPropertiesFail))
                )
            )
        );
    });

    loadPrimeNGConfig$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(loadAppContextSuccess),
                tap(() => {
                    this.i18nService.setTranslationsPrimeNG();
                })
            );
        },
        { dispatch: false }
    );

    searchTooltips$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(loadAppContextSuccess),
            switchMap(() =>
                this.tooltipService.searchTooltips().pipe(
                    map(tooltips => searchTooltipsSuccess({ tooltips })),
                    catchError(() => of(searchTooltipsFail))
                )
            )
        );
    });

    loadAppContextRedirect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(loadAppContextSuccess),
                tap(getAppContext => {
                    let user = getAppContext.appContext.user;
                    if (user && user.basicRole.roleKey === Roles.CREATE_USER) {
                        NavigationUtils.openUrl('/ts/#/welcome');
                    }
                })
            );
        },
        { dispatch: false }
    );

    displayToastSuccess$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(displayToastSuccess),
                tap(action => this.toastService.displayToastSuccess(action.message, action.detail))
            );
        },
        { dispatch: false }
    );

    displayToastWarning$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(displayToastWarning),
                tap(action => this.toastService.displayToastWarning(action.message, action.detail))
            );
        },
        { dispatch: false }
    );

    displayToastInfo$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(displayToastInfo),
                tap(action => this.toastService.displayToastInfo(action.message, action.detail))
            );
        },
        { dispatch: false }
    );

    displayToastError$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(displayToastError), //getActiveConfigVersionFail, getConfigVersionByIdFail
                tap(action => this.toastService.displayToastError(action.message, action.detail))
            );
        },
        { dispatch: false }
    );

    getAllFeatures$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(loadInitializerData),
            switchMap(() =>
                this.appFeatureService.getAllFeatures().pipe(
                    map(appFeatures => getAppFeaturesSuccess({ appFeatures })),
                    catchError((error: HttpErrorResponse) =>
                        of(
                            getAppFeaturesFail({
                                systemError: error.error as SystemError
                            })
                        )
                    )
                )
            )
        );
    });

    redirectToMyWorkplaceIfApplicable$ = createEffect(
        () => {
            return combineLatest([this.actions$.pipe(ofType(loadInitializerData)), this.store.select(selectMyWorkplaceFeatures)]).pipe(
                switchMap(([action, myWorkplaceFeatures]) => {
                    if (
                        !this.myWorkplaceService.isAppInsideMyWorkplace() &&
                        (myWorkplaceFeatures.myWorkplace?.enabled || myWorkplaceFeatures.myWorkplaceExternal?.enabled)
                    ) {
                        return this.myWorkplaceService.getUrls().pipe(
                            map((urls: MyWorkplaceUrls) => {
                                let url = null;

                                if (
                                    this.myWorkplaceService.isUrlComingFromExternalSERA(action.currentUrl) &&
                                    myWorkplaceFeatures.myWorkplaceExternal?.enabled
                                ) {
                                    url = urls.myWorkplaceBBAUrl;
                                } else if (this.myWorkplaceService.isUrlComingFromRegularSERA(action.currentUrl) && myWorkplaceFeatures.myWorkplace?.enabled) {
                                    url = urls.myWorkplaceUrl;
                                }

                                if (url !== null) {
                                    let currentUrlParts = action.currentUrl.split('/');
                                    currentUrlParts.splice(0, 3); // remove the first three parts (protocol and host)
                                    let currentUrlPath = '/' + currentUrlParts.join('/');
                                    NavigationUtils.openUrl(url + encodeURIComponent(currentUrlPath).replace(/%23/g, '%23').replace(/%2F/g, '%252F'));
                                }
                                return redirectToMyWorkplaceIfApplicableSuccess();
                            }),
                            catchError(() => of(redirectToMyWorkplaceIfApplicableFail()))
                        );
                    } else return of(redirectToMyWorkplaceIfApplicableSuccess());
                })
            );
        },
        { dispatch: false }
    );

    getExternalHelpLink$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(getExternalHelpLink),
            switchMap(action =>
                this.helpService.getExternalHelpLink(action.key).pipe(
                    map(externalLink => getExternalHelpLinkSuccess({ key: externalLink })),
                    catchError(() => of(displayToastError({ message: 'ERROR.HELP.LINK.TXT' })))
                )
            )
        );
    });

    getExternalHelpLinkSuccess$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(getExternalHelpLinkSuccess),
                tap(action => NavigationUtils.openUrlInNewTab('https://' + action.key['text' + this.translate.currentLang]))
            );
        },
        { dispatch: false }
    );

    copyCurrentUrlToClipboard$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(copyUrlToClipboard),
            concatLatestFrom(() => this.store.select(selectUserPrefs)),
            skipWhile(([_action, userPrefs]) => userPrefs === undefined),
            map(([action, userPrefs]) => {
                let currentUrl = action.url;
                if (currentUrl.includes('/analysis/') && userPrefs!.shareOption === 'SHARE_HIGHEST_VERSION') {
                    return copyUrlToClipboardMaxValidVersion({ url: currentUrl });
                }
                window.navigator.clipboard.writeText(currentUrl);
                return displayToastSuccess({ message: 'PAGE.TOP_RIGHT_NAV.SHARE_MSG.TXT' });
            })
        );
    });

    displayErrorDialogWithUUID$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(displayErrorDialogWithUUID),
            mergeMap(
                action =>
                    new Observable<Action>(observer => {
                        this.confirmationService.confirm({
                            key: 'dialogWithUUID',
                            header: action.uuid,
                            accept: () => {
                                navigator.clipboard.writeText(action.uuid.toString()).then(() => {
                                    observer.next(displayToastSuccess({ message: 'PAGE.UUID_CODE_COPIED.LBL' }));
                                    observer.complete();
                                });
                            },
                            reject: () => {
                                observer.complete();
                            }
                        });
                    })
            )
        );
    });

    newFromClipboard$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(newFromClipboard),
            switchMap(_ => {
                return this.analysisService.pasteAnalysis().pipe(
                    takeUntil(this.router.events.pipe(filter(event => event instanceof NavigationStart))),
                    map(result => {
                        if (result == null) {
                            return displayToastError({
                                message: 'ERROR.APP_WARN_EMPTY_CLIPBOARD.TXT'
                            });
                        }
                        return newFromClipboardLoaded({ analysis: result });
                    }),
                    catchError(error =>
                        of(
                            newFromClipboardFail({
                                systemError: {
                                    errCode: 'ERROR.' + error.error?.errCode + '.TXT',
                                    uuid: error.error?.uuid,
                                    errId: error.error?.errId
                                }
                            })
                        )
                    )
                );
            })
        );
    });

    navigateToClipboard$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(newFromClipboardLoaded),
            switchMap(action => {
                const analysis = AnalysisUtils.buildNewAnalysisFromClipboard(action.analysis);
                const routeSegment = HTTPUtils.getNewAnalysisRouteSegment(analysis.analysisType);
                return defer(() =>
                    this.router.navigate(['analysis', 'new', routeSegment, 'header'], {
                        queryParams: { from: 'clipboard' },
                        onSameUrlNavigation: 'reload',
                        info: newFromClipboard.type
                    })
                ).pipe(
                    filter(navigated => navigated),
                    map(() => newFromClipboardSuccess({ analysis })),
                    catchError(() => of(displayToastError({ message: 'MESSAGE.GENERAL.ERROR_OCCURED.TXT' }))),
                    takeUntil(this.actions$.pipe(ofType(newFromClipboard)))
                );
            })
        );
    });

    toastOnFailAction$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(newFromClipboardFail, exportToExcelFail),
            map(action => {
                if (ValidationUtils.isToShowDialogWithUUID(action.systemError)) {
                    return displayErrorDialogWithUUID({
                        title: action.systemError.errCode,
                        uuid: action.systemError.uuid!
                    });
                }
                return displayToastError({
                    message: action.systemError?.errCode
                        ? TranslationUtils.appendERRORPrefix(TranslationUtils.appendTxtSuffix(action.systemError.errCode))
                        : 'MESSAGE.GENERAL.ERROR_OCCURED.TXT'
                });
            })
        );
    });
}
